"use client"

import { css } from "@emotion/css"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import type { EmbedAttributes } from "@/../types/GutenbergBlockAttributes"
import BreakFromCentered from "@/shared-module/common/components/Centering/BreakFromCentered"
import { baseTheme } from "@/shared-module/common/styles/theme"
import { LoadingRegion, ErrorNotice } from "@/shared-module/components"
import aspectRatioFromClassName from "@/utils/course-material/aspectRatioFromClassName"
import { sanitizeCourseMaterialHtml } from "@/utils/course-material/sanitizeCourseMaterialHtml"

const VIMEO_MAX_WIDTH = 780

export const VimeoEmbedBlock: React.FC<
  React.PropsWithChildren<EmbedAttributes & { className?: string }>
> = (props) => {
  const [embedHtml, setEmbedHtml] = useState<string | undefined>(undefined)
  const [fetching, setFetching] = useState(true)
  const { t } = useTranslation()
  useEffect(() => {
    const fetchEmbed = async () => {
      if (props.url) {
        // maxWidth and maxHeight same as backend url_to_oembed_endpoint.rs
        const response = await fetch(
          `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
            props.url,
          )}&maxwidth=${VIMEO_MAX_WIDTH}&maxheight=440`,
        )
        if (!response.ok) {
          setFetching(false)
          return
        }
        const data = (await response.json()) as { html?: string }
        if (data.html) {
          setEmbedHtml(data.html)
        }
      }
      setFetching(false)
    }
    fetchEmbed()
  })

  return (
    <>
      {fetching && <LoadingRegion />}
      {embedHtml && !fetching && (
        <BreakFromCentered sidebar={false}>
          <figure
            className={css`
              width: 100%;
              max-width: ${VIMEO_MAX_WIDTH}px;
              margin: 4rem auto;
            `}
          >
            <div
              className={css`
                iframe {
                  display: block;
                  width: 100%;
                  aspect-ratio: ${aspectRatioFromClassName(props.className)};
                }
              `}
              dangerouslySetInnerHTML={{
                __html: embedHtml,
              }}
            ></div>
            <figcaption
              className={css`
                text-align: center;
                font-size: ${baseTheme.fontSizes[0]}px;
                margin-top: 0.5em;
                margin-bottom: 1em;
                color: ${baseTheme.colors.gray[400]};
              `}
              dangerouslySetInnerHTML={{ __html: sanitizeCourseMaterialHtml(props.caption ?? "") }}
            ></figcaption>
          </figure>
        </BreakFromCentered>
      )}
      {!embedHtml && !fetching && (
        <ErrorNotice
          error={t("could-not-fetch-embed", {
            provider: props.providerNameSlug,
            url: props.url ?? "undefined",
          })}
        />
      )}
    </>
  )
}
